import * as z from 'zod';
import { isoDateTimeSchema, strict, uuidSchema } from './common.js';
import { credentialTypeSchema } from './credential.js';
/**
 * AD-60 to AD-66: a box of pre-printed wristbands, as a batch of blanks.
 *
 * The facility's bands are printed in bulk by an outside supplier from a spreadsheet, weeks before
 * this software is deployed. A band therefore carries a code that exists before any person does,
 * and the only thing that makes it legitimate is that the batch was IMPORTED: a blank is a row
 * holding a keyed hash of the code and never the code, and claiming one is what turns it into a
 * credential.
 *
 * **Neither entity carries a personal value and neither carries ciphertext.** A batch is a print
 * run — a label, a count, a digest, a status — and a blank is a hash and a serial. That is why
 * neither table needs an AD-6 `TABLE_IDS` entry, and it is a property worth keeping: the moment a
 * batch grew an operator-written note it would be the only encrypted column on either table.
 *
 * **`codesDigest` is over the SORTED code list, and there is deliberately no per-code digest.**
 * Over the whole set it is not a confirmation oracle, because verifying it means already holding
 * all 2,200 codes. Per code it would be one, which is why that shape is forbidden rather than
 * merely unimplemented.
 */
/** Where a batch came from. The offline generator is the case that exists before deployment. */
export const batchSourceSchema = z.enum(['offline_import', 'in_app']);
/** AD-62: a whole batch can be withdrawn, which leaves already-claimed bands alone. */
export const batchStatusSchema = z.enum(['active', 'void']);
/** AD-62: a blank is single-use, so `claimed` is terminal and `void` is the other terminal. */
export const blankStatusSchema = z.enum(['available', 'claimed', 'void']);
/**
 * `SENTRA-2026-09-09-A`: quotable on a phone call with the supplier.
 *
 * Upper case, digits and hyphens only, which is also why AD-65 forbids it in audit detail: the
 * grammar `redact.ts` admits is lowercase, so a label handed to a detail is refused at run time.
 */
export const BATCH_LABEL_PATTERN = /^[A-Z0-9][A-Z0-9-]{2,39}$/u;
export const batchLabelSchema = z
    .string()
    .regex(BATCH_LABEL_PATTERN, { error: 'errors.validation.invalid_format' });
/** A SHA-256 digest as it travels over the wire: 64 lowercase hex characters. */
export const digestHexSchema = z
    .string()
    .regex(/^[\da-f]{64}$/u, { error: 'errors.validation.invalid_format' });
/** The largest batch this product will mint or import in one go. */
export const MAX_BATCH_CODES = 100_000;
export const batchCodeCountSchema = z.int().min(1).max(MAX_BATCH_CODES);
export const credentialBatchSchema = strict({
    id: uuidSchema,
    label: batchLabelSchema,
    type: credentialTypeSchema,
    source: batchSourceSchema,
    codeCount: batchCodeCountSchema,
    codesDigest: digestHexSchema,
    /** The digest of the supplier-facing manifest, when the import was given one to compare. */
    manifestDigest: digestHexSchema.nullable(),
    /** A PASS index version, never `NORMALIZE_VERSION` (AD-60). */
    indexVersion: z.int().nonnegative(),
    formatVersion: z.int().nonnegative(),
    generatedAt: isoDateTimeSchema.nullable(),
    importedAt: isoDateTimeSchema,
    status: batchStatusSchema,
    voidedAt: isoDateTimeSchema.nullable(),
    voidedBy: uuidSchema.nullable(),
    voidReason: z.string().max(200).nullable(),
    createdBy: uuidSchema.nullable(),
});
/**
 * One pre-printed band.
 *
 * There is no `code` field and there never can be: the row holds `lookup_index`, which is an HMAC,
 * and the only plaintext copy of the codes is the artifact AD-63 says to keep.
 */
export const credentialBlankSchema = strict({
    id: uuidSchema,
    batchId: uuidSchema,
    type: credentialTypeSchema,
    indexVersion: z.int().nonnegative(),
    /** Derived from the SORTED code order, so it is a property of the set and not of the file. */
    serial: z.int().positive(),
    status: blankStatusSchema,
    claimedAt: isoDateTimeSchema.nullable(),
    claimedCredentialId: uuidSchema.nullable(),
    voidedAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
});
/**
 * What is left in the box, which is what `passes.low_blank_threshold` is compared against.
 *
 * Counted per batch and overall, because "we are low" and "this batch is exhausted" are different
 * operational facts and the second is what tells an administrator which box to open next.
 */
export const credentialBatchInventorySchema = strict({
    batchId: uuidSchema,
    label: batchLabelSchema,
    status: batchStatusSchema,
    available: z.int().nonnegative(),
    claimed: z.int().nonnegative(),
    voided: z.int().nonnegative(),
});
/**
 * Importing a batch that already exists as a file.
 *
 * The codes are read from the file by the server rather than sent one by one, so what a caller
 * supplies is the label, the expected count and, when it has one, the manifest's digest to compare
 * against. `dryRun` reports the collisions without inserting anything, which is the form an
 * administrator uses before committing a box of two thousand bands.
 */
export const importCredentialBatchRequestSchema = strict({
    label: batchLabelSchema,
    type: credentialTypeSchema.default('qr'),
    source: batchSourceSchema.default('offline_import'),
    /** Compared against the recomputed digest; a mismatch refuses the whole import. */
    expectedCodesDigest: digestHexSchema.optional(),
    expectedCount: batchCodeCountSchema.optional(),
    dryRun: z.boolean().default(false),
});
/** Minting a batch inside the application, which AD-64 orders artifact-first. */
export const generateCredentialBatchRequestSchema = strict({
    count: batchCodeCountSchema,
    /**
     * Used as given when present. When omitted, the server takes `SENTRA-<UTC date>-<letter>` with the
     * first letter from A to Z that no batch of any status (a withdrawn one included), no generation
     * still in flight and no failed generation whose file was written already holds. A label that is
     * held, or a day whose twenty-six
     * letters all are, is refused with `CREDENTIAL_BATCH_LABEL_TAKEN` before anything is written.
     */
    label: batchLabelSchema.optional(),
    type: credentialTypeSchema.default('qr'),
    /** A fixed line of text printed beside every code, when the facility asks for one. */
    text2: z.string().min(1).max(64).optional(),
    serialColumn: z.boolean().default(true),
});
/** AD-62: withdrawing a whole box, which leaves the bands already in use alone. */
export const voidCredentialBatchRequestSchema = strict({
    reason: z.string().trim().min(1).max(200),
});
/**
 * AD-62: withdrawing one band, by serial or by the code printed on it.
 *
 * **It carries NO reason, and the emptiness is a correction rather than an omission.** This schema
 * declared a free-text reason and there was nowhere to put one: `credential_blanks` has no
 * `void_reason` column — only `credential_batches` does, because a whole box is withdrawn for a
 * stated reason and a single band is withdrawn because it is lost or damaged — and AD-65's audit
 * grammar admits a uuid, a lowercase schema identifier, a closed-set token, a count or a boolean,
 * so operator prose cannot travel in the detail either. A field that validates and is then dropped
 * is the exact defect the API phases exist to avoid, so the field is gone. `sentra credential-batch
 * void` already refuses `--reason` for a single band and says why in the same terms.
 *
 * The body is kept rather than removed, because the route is a `POST` and a strict empty object is
 * what refuses a caller that sends something: an absent schema would accept an unknown key and drop
 * it, which is the same defect in the other direction.
 */
export const voidCredentialBlankRequestSchema = strict({});
/**
 * The fields of a multipart import, which is how a batch reaches the API.
 *
 * The codes are read from the SUPPLIER'S FILE by the server rather than sent one by one, so what a
 * caller supplies beside the upload is the label, the expected count and, when it has one, the
 * manifest's digest to compare against. A multipart field is always a string on the wire, so the
 * count coerces here — which is the one place in this package that coerces, and it does so because
 * the alternative is a client that has to send a number as a number through a transport that has
 * no numbers.
 *
 * `dryRun` is deliberately NOT here: it is a query parameter
 * ({@link importCredentialBatchQuerySchema}), because it changes what the request DOES rather than
 * what it describes, and an administrator previewing a box of two thousand bands should be able to
 * see the difference in the URL.
 */
export const importCredentialBatchFieldsSchema = strict({
    label: batchLabelSchema,
    type: credentialTypeSchema.default('qr'),
    source: batchSourceSchema.default('offline_import'),
    expectedCodesDigest: digestHexSchema.optional(),
    expectedCount: z.coerce.number().int().min(1).max(MAX_BATCH_CODES).optional(),
});
/** `?dryRun=true` runs every check and reports what would happen, inserting nothing. */
export const importCredentialBatchQuerySchema = strict({
    dryRun: z
        .union([z.literal('true'), z.literal('false')])
        .default('false')
        .transform((value) => value === 'true'),
});
/** One code the import would collide with, and which table it is already in. */
export const credentialBatchCollisionSchema = strict({
    /** The one-based position in the SORTED code list, which is also the serial it would have had. */
    serial: z.int().positive(),
    where: z.enum(['blank', 'credential']),
});
/**
 * What an import did, or what a dry run would have done.
 *
 * `outcome` is the whole answer and the three members are not interchangeable.
 * `already_imported` is the idempotent case: this exact set of codes is already in the database, so
 * nothing was written and nothing needed to be. `dry_run` reports what a real run would do.
 * `imported` is the only one that wrote anything, and an import is all-or-nothing, because a
 * half-imported batch means bands in a box the desk refuses with no way to tell which.
 */
export const importCredentialBatchReportSchema = strict({
    outcome: z.enum(['already_imported', 'dry_run', 'imported']),
    batchId: uuidSchema.nullable(),
    label: batchLabelSchema,
    codesDigest: digestHexSchema,
    count: batchCodeCountSchema,
    inserted: z.int().nonnegative(),
    /** Unclaimed bands across every ACTIVE batch afterwards, which is what the low-stock alert reads. */
    available: z.int().nonnegative(),
    collisions: z.array(credentialBatchCollisionSchema),
    stockAlertChanged: z.boolean(),
});
/** A batch, with what is left in its box. */
export const credentialBatchWithInventorySchema = strict({
    batch: credentialBatchSchema,
    inventory: credentialBatchInventorySchema,
});
/**
 * The list, with the installation-wide figures Section 1.12's pass-stock panel needs.
 *
 * `available` here is the same number `passes.low_blank_threshold` is compared against: unclaimed
 * bands across every batch that is still ACTIVE. A blank of a voided batch is not stock, and
 * counting it would keep the low-stock alert quiet exactly when a whole box has been withdrawn.
 */
export const credentialBatchListSchema = strict({
    items: z.array(credentialBatchWithInventorySchema),
    totals: strict({
        available: z.int().nonnegative(),
        claimed: z.int().nonnegative(),
        voided: z.int().nonnegative(),
        threshold: z.int().nonnegative(),
        low: z.boolean(),
    }),
});
/** What withdrawing a whole box did. Already-claimed bands are untouched by design (AD-62). */
export const voidCredentialBatchReportSchema = strict({
    batchId: uuidSchema,
    blanksVoided: z.int().nonnegative(),
    available: z.int().nonnegative(),
    stockAlertChanged: z.boolean(),
});
/** What withdrawing one band did. */
export const voidCredentialBlankReportSchema = strict({
    batchId: uuidSchema,
    blankId: uuidSchema,
    serial: z.int().positive(),
    available: z.int().nonnegative(),
    stockAlertChanged: z.boolean(),
});
/**
 * What asking for a batch to be MINTED answers with, immediately.
 *
 * Generation is a job and not a request (AD-64): minting, auditing, writing the artifact, re-reading
 * it and then inserting N blanks is not something to hold a socket open for, and the ordering is
 * load-bearing — the file is written and re-read BEFORE the transaction, so a failed commit leaves
 * a file on disk whose path the job reports and which the ordinary import route can finish. So the
 * route answers 202 with the export row the artifact will be recorded under, and the panel polls
 * it: `status` moves `queued` to `running` to `ready`, and `ready` is when the download works.
 */
export const generateCredentialBatchAcceptedSchema = strict({
    exportId: uuidSchema,
    label: batchLabelSchema,
    count: batchCodeCountSchema,
    status: z.enum(['queued', 'running', 'ready', 'failed', 'expired']),
    /**
     * The one-time password the artifact is sealed under, shown exactly once (AD-63).
     *
     * It is in this body and in no other place: nothing stores it and no later `GET` can produce it,
     * so an administrator who loses it loses the box's only plaintext copy. That is stated in the
     * instruction sheet the generator emits, which tells the facility to keep theirs and the supplier
     * to destroy the one they printed from.
     */
    password: z.string().min(16).max(128),
});
//# sourceMappingURL=batch.js.map